// src/admin/pages/audit/auditService.ts
// Central activity/audit trail. Every module that creates, edits, or deletes
// a record should call AuditService.log(...) once the Firestore write succeeds.
// This gives a single, searchable "who did what, and when" history across the
// entire dealership system (cars, imports, reservations, sold vehicles, etc.).
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  getCountFromServer,
  serverTimestamp,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';

const AUDIT_COLLECTION = 'auditLogs';

export type AuditAction = 'create' | 'update' | 'delete' | 'status_change' | 'login' | 'other';

export interface AuditLog {
  id?: string;
  action: AuditAction;
  module: string; // e.g. 'Car Inventory', 'Import Orders', 'Employees'
  entityId?: string;
  entityLabel?: string; // human-readable name, e.g. the car title or employee name
  description: string; // "Created car '2021 Toyota Aqua'"
  userId: string;
  userName: string;
  createdAt: Timestamp | Date;
}

export class AuditService {
  /** Fire-and-forget logger. Never throws — a logging failure must never
   *  block the actual business operation that triggered it. */
  static async log(entry: Omit<AuditLog, 'id' | 'createdAt'>): Promise<void> {
    try {
      await addDoc(collection(db, AUDIT_COLLECTION), {
        ...entry,
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to write audit log (non-blocking):', error);
    }
  }

  static async fetchLogs(
    pageSize: number = 25,
    page: number = 1,
    moduleFilter?: string,
    actionFilter?: string
  ): Promise<{ logs: AuditLog[]; totalCount: number }> {
    try {
      const colRef = collection(db, AUDIT_COLLECTION);
      const constraints: any[] = [];
      if (moduleFilter && moduleFilter !== 'all') constraints.push(where('module', '==', moduleFilter));
      if (actionFilter && actionFilter !== 'all') constraints.push(where('action', '==', actionFilter));

      const countSnap = await getCountFromServer(query(colRef, ...constraints));
      const totalCount = countSnap.data().count;

      const orderConstraint = orderBy('createdAt', 'desc');
      const skip = (page - 1) * pageSize;

      let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
      if (skip > 0) {
        const seek = await getDocs(query(colRef, ...constraints, orderConstraint, limit(skip)));
        if (!seek.empty) cursor = seek.docs[seek.docs.length - 1];
      }

      const pageQuery = cursor
        ? query(colRef, ...constraints, orderConstraint, startAfter(cursor), limit(pageSize))
        : query(colRef, ...constraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);
      const logs: AuditLog[] = snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          action: data.action,
          module: data.module,
          entityId: data.entityId,
          entityLabel: data.entityLabel,
          description: data.description,
          userId: data.userId,
          userName: data.userName,
          createdAt: data.createdAt?.toDate() || new Date(),
        };
      });

      return { logs, totalCount };
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return { logs: [], totalCount: 0 };
    }
  }

  static readonly MODULES = [
    'Car Inventory', 'Car Brands', 'Category', 'Models & Trims', 'Branches',
    'Import Orders', 'Costs', 'Suppliers', 'Reservations', 'Trade-Ins', 'Sold Vehicles',
    'Employees', 'Users', 'Roles', 'Other',
  ];
}
